import {Observable, Subject, Subscription, tap, timer} from "rxjs";

/**
 * Anything the scheduler can place in time: a duration in beats.
 * Tools extend this with their own payload (click type, pitch, ...).
 */
interface SchedulableEvent {
    /** Until the next event, in beats. */
    duration: number;
}

/** A {@link SchedulableEvent} placed on the audio timeline. */
interface ScheduledEvent<TEvent> {
    /** Until the next event, in seconds. */
    duration: number;
    timestamp: number;
    event: TEvent;
}

/**
 * Produces the next batch of events (e.g. one bar's worth) on demand.
 * Called synchronously, only when the scheduler's buffer runs dry: generation
 * is demand-driven, so no events are generated before they are needed.
 */
type EventBatchSource<TEvent> = () => TEvent[];

interface SchedulerConfig<TEvent> {
    /**
     * Live tempo read, consulted per scheduled event, so tempo changes apply
     * within the look-ahead window.
     */
    bpm: () => number;
    source: EventBatchSource<TEvent>;
}

/**
 * Owns event buffering and look-ahead scheduling.
 *
 * Pulls batches of events from the {@link EventBatchSource} into a buffer,
 * and every `lookAhead` milliseconds converts enough of them into timestamped
 * events to fill the `scheduleAheadTime` window, publishing them on
 * {@link events$}. Timestamps are derived from the injected clock, so the
 * caller decides which clock the schedule runs against.
 *
 * Durations arrive in beats and are converted to seconds here, so tempo
 * changes apply live, within the look-ahead window. Structural changes take
 * effect when the next batch is pulled from the source.
 *
 * This is deliberately pull-based (the classic Web Audio "two clocks"
 * pattern): audio scheduling must run ahead of wall-clock time, which a
 * push-based stream cannot do on its own.
 */
class Scheduler<TEvent extends SchedulableEvent> {

    private readonly config: SchedulerConfig<TEvent>;
    private readonly currentTime: () => number;

    // how often to call schedule(), milliseconds
    private readonly lookAhead: number = 25;
    // how far ahead to schedule audio, seconds
    private readonly scheduleAheadTime: number = 0.1;
    private schedulerSubscription: Subscription | undefined;

    private buffer: TEvent[] = [];

    // the most recently scheduled event, used to chain the next timestamp;
    // also holds a zero-duration anchor before anything is scheduled
    private lastScheduled: {duration: number; timestamp: number} | undefined;

    private readonly eventSubject = new Subject<ScheduledEvent<TEvent>>();
    readonly events$: Observable<ScheduledEvent<TEvent>> = this.eventSubject;

    private scheduler$ = timer(0, this.lookAhead)
        .pipe(
            tap(() => this.schedule()),
        );

    /**
     * Duration of one beat in seconds. Read live so tempo changes apply to
     * the next scheduled event, within the look-ahead window.
     */
    private get beatDuration(): number {
        return 60 / this.config.bpm();
    }

    constructor(config: SchedulerConfig<TEvent>, currentTime: () => number) {
        this.config = config;
        this.currentTime = currentTime;
    }

    /**
     * Emit the seed events (each with its duration in beats, e.g. a count-in)
     * and start the look-ahead loop, which chains off the last seed's
     * timestamp. With an empty seed, generation starts one
     * `scheduleAheadTime` from now.
     */
    start(seed: TEvent[] = []) {
        let timestamp = this.currentTime() + this.scheduleAheadTime;

        // anchor for the schedule loop to chain from; overwritten by each seed
        this.lastScheduled = {duration: 0, timestamp};

        for (const event of seed) {
            const duration = event.duration * this.beatDuration;
            this.lastScheduled = {duration, timestamp};
            this.eventSubject.next({duration, timestamp, event});
            timestamp += duration;
        }

        this.schedulerSubscription = this.scheduler$.subscribe();
    }

    /**
     * Stop scheduling and clear the buffered events, so the next start begins
     * cleanly on a batch boundary.
     */
    stop() {
        this.schedulerSubscription?.unsubscribe();
        this.buffer = [];
        this.lastScheduled = undefined;
    }

    private schedule() {
        const previous = this.lastScheduled;
        if (!previous) {
            return;
        }

        let nextTimestamp = previous.timestamp + previous.duration;

        while (nextTimestamp < (this.currentTime() + this.scheduleAheadTime)) {
            const event = this.nextEvent();
            const duration = event.duration * this.beatDuration;

            const scheduled = {duration, timestamp: nextTimestamp, event};
            this.lastScheduled = {duration, timestamp: nextTimestamp};
            this.eventSubject.next(scheduled);

            nextTimestamp += duration;
        }
    }

    private nextEvent(): TEvent {
        if (this.buffer.length === 0) {
            // synchronously refills the buffer with a fresh batch
            this.buffer = this.config.source();
        }
        return this.buffer.shift()!;
    }
}

export {Scheduler, SchedulerConfig, SchedulableEvent, ScheduledEvent, EventBatchSource};
